import { ExclamationTriangleIcon } from '@protoss/ui';
import * as React from 'react';
import { formatDateTime } from '../../../lib/datetime';
import { formatDurationRange } from '../../../lib/format';
import type { PipelineRunItem, PipelineRunStageSummary } from '../types/source-system';

// ── Helpers ──────────────────────────────────────────────────────────────────

/** Map artifact.status to a CSS modifier and display label. */
function runStatusInfo(status: string | null): { mod: string; label: string } {
  switch (status) {
    case 'done':
      return { mod: 'success', label: 'Succeeded' };
    case 'in_progress':
      return { mod: 'active', label: 'In Progress' };
    case 'open':
      return { mod: 'pending', label: 'Pending' };
    case 'cancelled':
      return { mod: 'cancelled', label: 'Cancelled' };
    case 'closed':
      return { mod: 'failed', label: 'Failed' };
    default:
      return { mod: 'unknown', label: status ?? 'Unknown' };
  }
}

// ── Stage tree ────────────────────────────────────────────────────────────────

/** Single stage row inside the expanded run detail. */
function StageRow({ stage, depth }: { stage: PipelineRunStageSummary; depth: number }) {
  const { mod, label } = runStatusInfo(
    stage.result
      ? stage.result === 'succeeded'
        ? 'done'
        : stage.result === 'canceled'
          ? 'cancelled'
          : 'closed'
      : stage.status === 'inProgress'
        ? 'in_progress'
        : stage.status === 'pending'
          ? 'open'
          : null
  );

  return (
    <div
      className="pl-run-stage__row"
      style={{ '--pl-stage-depth': depth } as React.CSSProperties}
      data-type={stage.type.toLowerCase()}
    >
      <span className={`pl-run-stage__dot pl-run-stage__dot--${mod}`} aria-hidden="true" />
      <span className="pl-run-stage__name">{stage.name}</span>
      {stage.workerName && <span className="pl-run-stage__worker">{stage.workerName}</span>}
      <span className={`pl-run-stage__status pl-run-stage__status--${mod}`}>{label}</span>
      {stage.errorIssuesCount > 0 && (
        <span className="pl-run-stage__errors">
          <ExclamationTriangleIcon aria-hidden="true" />
          {stage.errorIssuesCount}
        </span>
      )}
    </div>
  );
}

/** Recursive tree rendering for stages. */
function StageTree({
  stages,
  parentId = null,
  depth = 0,
}: {
  stages: PipelineRunStageSummary[];
  parentId?: string | null;
  depth?: number;
}) {
  // Sort children by order
  const children = stages.filter((s) => s.parentId === parentId).sort((a, b) => a.order - b.order);

  if (children.length === 0) return null;

  return (
    <>
      {children.map((stage) => (
        <React.Fragment key={stage.id}>
          <StageRow stage={stage} depth={depth} />
          <StageTree stages={stages} parentId={stage.id} depth={depth + 1} />
        </React.Fragment>
      ))}
    </>
  );
}

// ── PipelineRunTableHeader ─────────────────────────────────────────────────────────

export function PipelineRunTableHeader() {
  return (
    <div className="ch-table__header pl-run-table" aria-hidden="true">
      <div className="ch-table__col">Status</div>
      <div className="ch-table__col">Date / Time</div>
      <div className="ch-table__col">Requested By</div>
      <div className="ch-table__col">Duration</div>
      <div className="ch-table__col">Run Name</div>
    </div>
  );
}

/** Content rendered in the SplitPaneLayout detail pane. */
export function PipelineRunDetail({ run }: { run: PipelineRunItem }) {
  const { mod, label } = runStatusInfo(run.status);
  const duration = formatDurationRange(run.startedAt, run.finishedAt);

  return (
    <div className="pl-run-detail">
      <div className="pl-run-detail__meta">
        <span className={`pl-run-row__badge pl-run-row__badge--${mod}`}>{label}</span>
        {run.requestedBy && <span className="pl-run-detail__actor">{run.requestedBy}</span>}
        {run.startedAt && (
          <span className="pl-run-detail__time">{formatDateTime(run.startedAt)}</span>
        )}
        {duration && <span className="pl-run-detail__duration">{duration}</span>}
        {run.stageCount > 0 && (
          <span className="pl-run-detail__stages">{run.stageCount} stages</span>
        )}
      </div>

      {run.externalUrl && (
        <a
          href={run.externalUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="pl-run-detail__link"
        >
          Open in ADO ↗
        </a>
      )}

      {run.stages.length > 0 && (
        <div className="pl-run-detail__section">
          <p className="pl-run-row__detail-label">Stages</p>
          <div className="pl-run-row__stage-tree">
            <StageTree stages={run.stages} />
          </div>
        </div>
      )}

      {run.artifactNames.length > 0 && (
        <div className="pl-run-detail__section">
          <p className="pl-run-row__detail-label">Artifacts</p>
          <ul className="pl-run-row__artifact-list">
            {run.artifactNames.map((name) => (
              <li key={name} className="pl-run-row__artifact-item">
                {name}
              </li>
            ))}
          </ul>
        </div>
      )}

      {run.failedLogContent && (
        <div className="pl-run-detail__section">
          <p className="pl-run-row__detail-label">
            <ExclamationTriangleIcon aria-hidden="true" className="pl-run-row__log-icon" />
            Failure log
          </p>
          <pre className="pl-run-row__log-content">{run.failedLogContent}</pre>
        </div>
      )}
    </div>
  );
}

// ── PipelineRunRow ─────────────────────────────────────────────────────────────

interface PipelineRunRowProps {
  run: PipelineRunItem;
  selected: boolean;
  onSelect: (uuid: string) => void;
}

/**
 * Clickable table row. Columns: Status | Date/Time | Requested By | Duration | Run Name.
 */
export function PipelineRunRow({ run, selected, onSelect }: PipelineRunRowProps) {
  const { mod, label } = runStatusInfo(run.status);
  const duration = formatDurationRange(run.startedAt, run.finishedAt);

  return (
    <div
      className={`ch-table__row pl-run-table ch-item--clickable${selected ? ' ch-item--selected' : ''}`}
      role="button"
      tabIndex={0}
      onClick={() => onSelect(run.uuid)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onSelect(run.uuid);
      }}
      aria-pressed={selected}
    >
      <div className="ch-table__col">
        <span className={`pl-run-row__badge pl-run-row__badge--${mod}`}>{label}</span>
      </div>
      <div className="ch-table__col ch-table__col--date">{formatDateTime(run.startedAt)}</div>
      <div className="ch-table__col ch-table__col--author">{run.requestedBy ?? '-'}</div>
      <div className="ch-table__col ch-table__col--date">{duration || '-'}</div>
      <div className="ch-table__col ch-table__col--title">{run.runName}</div>
    </div>
  );
}
